import { useState, useMemo, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { t } from 'utilities/TranslationUtils';
import { AppButton } from 'components/form';
import { LoadingOverlay } from 'components/ui';

import ChartHint from './ChartHint';
import KickDetailPanel from './KickDetailPanel';
import LayoutFilterPill from './LayoutFilterPill';
import ChartSwitcherCard from './ChartSwitcherCard';
import KickStatsSummaryRow from 'pages/Sessions/components/KickStatsSummaryRow';
import PitchPreviewCard from 'pages/Sessions/components/PitchPreviewCard';
import MeasurementsTable from 'pages/Sessions/components/MeasurementsTable';
import OnTargetTrend from './OnTargetTrend';
import FreeKickZonePitch from './FreeKickZonePitch';
import CornerZonePitch from './CornerZonePitch';
import KicksPerSessionGoalRateChart from './KicksPerSessionGoalRateChart';
import RestGapGoalRateChart from './RestGapGoalRateChart';
import MetricsCompareChart from './MetricsCompareChart';
import ResultBreakdownChart from './ResultBreakdownChart';
import PlayerPerformanceActivityLast30Days from './PlayerPerformanceActivityLast30Days';
import SessionTypeTabs from './SessionTypeTabs';
import PlayerPerformanceDisciplineCard from './PlayerPerformanceDisciplineCard';
import {
  GENERAL_SESSION_TYPE,
  playerSessionTypeTabs,
  buildPlayerGeneralSummary,
  buildPlayerDisciplineSummaries,
  computeKickTileStats,
  kickNoun,
  buildOnTargetTrendPoints,
  getCornerZoneIdForLayout,
  getFreeKickZoneIdForLayout,
  filterByTimeframe,
  resolveLayoutId,
  resolveCornerSide,
  passesQuickFilters,
  sessionKickCount,
  toResultFilterKey,
  cornerSideOf,
  FavoriteFilter,
  TrendGroupBy,
  TREND_GROUP_OPTIONS,
} from './playerPerformanceUtils';
import {
  KIND_OPTIONS,
  KIND_CAPABILITIES,
  MeasurementMarkerDto,
  SessionKindGroupName,
  SessionKindGroupKey,
  prefersTopView,
  FreeKickZoneDto,
  CornerZoneLayoutDto,
} from 'types/models/SessionsPageData';
import { formatSessionFilterLabel } from 'utilities/DateUtils';


type FavoriteState = FavoriteFilter;


interface PlayerPerformanceViewProps {
  sessionTypeFilter: string;
  onSelectSessionType: (filterValue: string) => void;
  /** True while this player's report is loading for the first time — shows an inline spinner
   *  in the report area while the player identity header stays visible. */
  isLoading?: boolean;
  /** Error to show in the report area when the player's report genuinely failed to load. */
  loadError?: string | null;
  /** True when the player's snapshot is still generating downstream (retryable, not a failure). */
  notReady?: boolean;
  /** Retry the player load (used by the not-ready / error states). */
  onRetry?: () => void;
}

// Stable defaults so memoised children keep their identity when the store has no zones yet.
const NO_ZONES: FreeKickZoneDto[] = [];
const NO_CORNER_LAYOUTS: CornerZoneLayoutDto[] = [];

/** Column-width transition (0.35s in the stylesheet) plus a beat for the charts to re-measure. */
const LAYOUT_MOVE_MS = 480;
/** Most sessions loaded for the selected kicks' flight paths at once. */
const MAX_TRAJECTORY_SESSIONS = 8;

const CHART_TABS = [
  { key: 'trend', labelKey: 'playerPerformance.chartTabTrend' },
  { key: 'kicksPerSession', labelKey: 'playerPerformance.chartTabKicksPerSession' },
  { key: 'restGap', labelKey: 'playerPerformance.chartTabRestGap' },
  { key: 'results', labelKey: 'playerPerformance.chartTabResults' },
  { key: 'compare', labelKey: 'playerPerformance.chartTabCompare' },
] as const;
type ChartTab = typeof CHART_TABS[number]['key'];

const PlayerPerformanceView: React.FunctionComponent<PlayerPerformanceViewProps> = observer(({
  sessionTypeFilter, onSelectSessionType, isLoading = false, loadError = null,
  notReady = false, onRetry,
}) => {
  const { playerPerformanceStore, apiServiceAxiosInstance: api } = useStores();

  // All hooks must come before the early return guard
  const [result, setResult] = useState<string[]>([]);
  const [favorite, setFavorite] = useState<FavoriteState>('all');
  const [trendGroupBy, setTrendGroupBy] = useState<TrendGroupBy>('sessions');
  const [sideFilter, setSideFilter] = useState<Set<'left' | 'right'>>(new Set());
  const [selectedZones, setSelectedZones] = useState<Set<number>>(new Set());
  // Wide screens: true shrinks the kicks view to half its width so the charts get more room.
  const [chartsFocused, setChartsFocused] = useState(false);
  // While the columns change width the kicks chart can't follow frame by frame (ECharts only re-measures once the size
  // settles), so it fades out for the move and back in at the new size instead of showing a stale, stretched canvas.
  const [layoutMoving, setLayoutMoving] = useState(false);
  const movingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markLayoutMoving = () => {
    setLayoutMoving(true);
    if (movingTimer.current) clearTimeout(movingTimer.current);
    movingTimer.current = setTimeout(() => setLayoutMoving(false), LAYOUT_MOVE_MS);
  };
  useEffect(() => () => { if (movingTimer.current) clearTimeout(movingTimer.current); }, []);
  const [chartSlots, setChartSlots] = useState<ChartTab[]>(['trend']);
  // The overview card can be folded down to its filter row; comparing two charts folds it by default.
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);

  const {
    focusedPlayer, measurements: allMeasurements, sessions, focusedPlayerId,
    freeKickZones = NO_ZONES, cornerZoneLayouts = NO_CORNER_LAYOUTS,
    timeframe,
  } = playerPerformanceStore;

  // Timeframe scopes everything on the page.
  const measurements = useMemo(() => filterByTimeframe(allMeasurements, timeframe), [allMeasurements, timeframe]);

  const disciplineSummaries = useMemo(
    () => (focusedPlayerId ? buildPlayerDisciplineSummaries(measurements, focusedPlayerId) : []),
    [measurements, focusedPlayerId],
  );

  // Unfiltered on purpose: feeds the "last 30 days" activity strip, which has its own fixed window.
  const focusedPlayerMeasurements = useMemo(
    () => (focusedPlayerId ? allMeasurements.filter((measurement) => measurement.playerId === focusedPlayerId) : []),
    [allMeasurements, focusedPlayerId],
  );

  const generalSummary = useMemo(
    () => (focusedPlayerId ? buildPlayerGeneralSummary(measurements, focusedPlayerId) : null),
    [measurements, focusedPlayerId],
  );

  const tabs = useMemo(
    () => (focusedPlayerId ? playerSessionTypeTabs(measurements, focusedPlayerId) : []),
    [measurements, focusedPlayerId],
  );

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.filterValue === sessionTypeFilter) ?? tabs[0] ?? null,
    [tabs, sessionTypeFilter],
  );

  const isGeneralTab = activeTab?.filterValue === GENERAL_SESSION_TYPE;

  const activeKind: SessionKindGroupName = useMemo(
    () => activeTab?.kind ?? 'FreeKick',
    [activeTab],
  );

  const activeKey: SessionKindGroupKey = useMemo(() => {
    const option = KIND_OPTIONS.find((o) => o.group === activeKind);
    return option?.value ?? 'freekick';
  }, [activeKind]);

  const activeTabLabel = useMemo(() => {
    if (!activeTab) return '';
    return t(activeTab.labelKey);
  }, [activeTab]);

  const playerKindMeasurements = useMemo(
    () => (focusedPlayerId
      ? measurements.filter((m) => m.playerId === focusedPlayerId && m.kind === activeKind)
      : []),
    [measurements, focusedPlayerId, activeKind],
  );

  // For corner zones: determine which side to show by default
  // Default to Left unless Left has 0 kicks, then show Right
  const cornerLeftKicks = useMemo(
    () => playerKindMeasurements.filter((m) => (m.kickZ ?? 0) > 0).length,
    [playerKindMeasurements],
  );

  // Layout / side preferences live in the store so they stick across player and session-type switches. They are
  // only honoured when valid for the current context (layout must exist, side must have kicks); otherwise the
  // default applies without discarding the preference.
  const { freeKickLayoutId, cornerLayoutId, cornerSide } = playerPerformanceStore;
  const selectedFreeKickLayoutId = resolveLayoutId(freeKickLayoutId, freeKickZones.map((z) => z.layoutId), '1');
  const cornerLayoutIds = cornerZoneLayouts.map((l) => l.layoutId);
  // Corners default to the second layout when present (the first is the legacy template).
  const defaultCornerLayoutId = cornerLayoutIds[1] ?? cornerLayoutIds[0] ?? '1';
  const selectedCornerLayoutId = resolveLayoutId(cornerLayoutId, cornerLayoutIds, defaultCornerLayoutId);
  const selectedCornerSide = resolveCornerSide(cornerSide, cornerLeftKicks);

  // Zone ids are layout-specific, so a zone selection never carries over to another player / session type.
  useEffect(() => {
    setSelectedZones(new Set());
  }, [sessionTypeFilter, focusedPlayerId]);

  // "Filter only this session" (from the kick popup): narrows every chart and the table to one session.
  const [sessionFilter, setSessionFilter] = useState<{ id: string; label: string } | null>(null);
  useEffect(() => {
    setSessionFilter(null);
  }, [sessionTypeFilter, focusedPlayerId]);
  const filterToSession = (m: MeasurementMarkerDto) => {
    const capturedAtUtc = sessions.find((s) => s.id === m.sessionId)?.capturedAtUtc ?? m.capturedAtUtc;
    setSessionFilter({ id: m.sessionId, label: formatSessionFilterLabel(capturedAtUtc) });
  };
  const sessionScopedMeasurements = useMemo(
    () => (sessionFilter ? playerKindMeasurements.filter((m) => m.sessionId === sessionFilter.id) : playerKindMeasurements),
    [playerKindMeasurements, sessionFilter],
  );

  const scopedMeasurements = useMemo(() => {
    let result = sessionScopedMeasurements;

    // For Corner, always filter by the selected side (Left: kickZ > 0, Right: kickZ < 0)
    if (activeKind === 'Corner') {
      result = result.filter((m) => (selectedCornerSide === 'Left' ? (m.kickZ ?? 0) > 0 : (m.kickZ ?? 0) < 0));
    }

    if (selectedZones.size > 0) {
      result = result.filter((m) => {
        const mappedCornerZoneId = getCornerZoneIdForLayout(m.cornerZones, selectedCornerLayoutId);
        if (mappedCornerZoneId != null) return selectedZones.has(mappedCornerZoneId);
        const mappedFreeKickZoneId = activeKind === 'FreeKick' ? getFreeKickZoneIdForLayout(m, selectedFreeKickLayoutId) : undefined;
        if (mappedFreeKickZoneId != null) return selectedZones.has(mappedFreeKickZoneId);
        return false;
      });
    }

    return result;
  }, [sessionScopedMeasurements, selectedZones, selectedCornerLayoutId, selectedFreeKickLayoutId, activeKind, selectedCornerSide]);

  const playerKindSessionCount = useMemo(
    () => new Set(scopedMeasurements.map((m) => m.sessionId).filter((id): id is string => Boolean(id))).size,
    [scopedMeasurements],
  );

  const resultOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const m of playerKindMeasurements) {
      const key = toResultFilterKey(m.kind, m.result);
      if (!seen.has(key)) {
        seen.add(key);
        options.push({ value: key, label: `${activeTabLabel} - ${m.result}` });
      }
    }
    return options;
  }, [playerKindMeasurements, activeTabLabel]);

  const previewMeasurements = useMemo(
    () => scopedMeasurements.filter((m) => passesQuickFilters(m, result, favorite, sideFilter)),
    [scopedMeasurements, result, favorite, sideFilter],
  );
  // The favourite filter only makes sense when this player has starred kicks of this kind.
  const hasFavorites = useMemo(() => playerKindMeasurements.some((m) => m.isFavorite === true), [playerKindMeasurements]);
  const zonePitchMeasurements = useMemo(
    () => sessionScopedMeasurements.filter((m) => passesQuickFilters(m, result, favorite, sideFilter)),
    [sessionScopedMeasurements, result, favorite, sideFilter],
  );

  // The kick opened from the table: shown in a panel beside it (while it is still among the listed kicks).
  const [panelKickId, setPanelKickId] = useState<string | null>(null);
  const [pitchKey, setPitchKey] = useState(0);
  const [pitchComparisonId, setPitchComparisonId] = useState<string | null>(null);
  // Drag-selection on the goal/pitch view. When non-empty, the stat cells below the view
  // recompute for the selected kicks only.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const pitchPreviewRef = useRef<HTMLDivElement>(null);

  // Picking kicks one by one also opens the latest one in the table's detail panel; deselecting the open kick falls
  // back to the previously picked one (or closes the panel).
  const toggleKick = (id: string) => {
    const next = new Set(selectedIds);
    const adding = !next.has(id);
    if (adding) next.add(id); else next.delete(id);
    setSelectedIds(next);
    if (adding) setPanelKickId(id);
    else if (panelKickId === id) setPanelKickId(Array.from(next).pop() ?? null);
  };
  const addSelected = (ids: string[]) => setSelectedIds((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  // The top view draws a selected kick's flight path; the snapshot rows have none, so the selected kicks' sessions are
  // loaded (once each) and their paths overlaid.
  useEffect(() => {
    if (!api || selectedIds.size === 0) return;
    const firstKickBySession = new Map<string, string>();
    previewMeasurements.forEach((m) => {
      if (selectedIds.has(m.id) && !firstKickBySession.has(m.sessionId)) firstKickBySession.set(m.sessionId, m.id);
    });
    Array.from(firstKickBySession).slice(0, MAX_TRAJECTORY_SESSIONS).forEach(([sessionId, kickId]) => {
      playerPerformanceStore.loadSessionTrajectories(api, sessionId, kickId);
    });
  }, [api, selectedIds, previewMeasurements, playerPerformanceStore]);
  // Read during render (not only inside the memo) so the view re-renders as each session's paths arrive.
  const trajectoryCount = playerPerformanceStore.trajectoryByMeasurementId.size;
  // With kicks picked by hand the picture shows just those, like the table and the charts.
  const topViewMeasurements = useMemo(
    () => (selectedIds.size > 0 ? previewMeasurements.filter((m) => selectedIds.has(m.id)) : previewMeasurements).map((m) => {
      // Only selected kicks draw a path, so only they get a new object; the rest keep their identity (and the chart's
      // memoised inputs).
      const samples = selectedIds.has(m.id) && !m.trajectorySamples ? playerPerformanceStore.trajectoryByMeasurementId.get(m.id) : undefined;
      return samples ? { ...m, trajectorySamples: samples } : m;
    }),
    [previewMeasurements, selectedIds, trajectoryCount, playerPerformanceStore],
  );

  // A manual selection (dragged on the picture) is one more filter: the table, like the tiles, shows just those kicks.
  const tableMeasurements = useMemo(
    () => (selectedIds.size > 0 ? previewMeasurements.filter((m) => selectedIds.has(m.id)) : previewMeasurements),
    [previewMeasurements, selectedIds],
  );

  // The charts follow a manual selection too: with kicks picked, they show only those.
  const chartMeasurements = useMemo(
    () => (selectedIds.size > 0 ? scopedMeasurements.filter((m) => selectedIds.has(m.id)) : scopedMeasurements),
    [scopedMeasurements, selectedIds],
  );
  const chartTrendPoints = useMemo(() => buildOnTargetTrendPoints(chartMeasurements, 1), [chartMeasurements]);

  const panelKick = useMemo(
    () => (panelKickId ? tableMeasurements.find((m) => m.id === panelKickId) ?? null : null),
    [panelKickId, tableMeasurements],
  );
  const panelSessionKickCount = useMemo(() => (panelKick ? sessionKickCount(measurements, panelKick) : 0), [panelKick, measurements]);


  // The stat tiles describe exactly the kicks the table shows.
  const rowStats = useMemo(() => computeKickTileStats(tableMeasurements, activeKind), [tableMeasurements, activeKind]);

  // When zone selection changes, scroll the zone chart into view with a small offset from the top.
  const toggleZone = (zoneId: number) => {
    setSelectedZones((prev) => (prev.has(zoneId) && prev.size === 1 ? new Set<number>() : new Set<number>([zoneId])));
  };
  const clearZones = () => {
    setSelectedZones(new Set());
  };

  useEffect(() => {
    setPitchKey(0);
    setPitchComparisonId(null);
    setSelectedIds(new Set());
    // Reset side filter when switching session types
    setSideFilter(new Set());
    setSelectedZones(new Set());
  }, [activeKey, focusedPlayerId]);

  // Which zone pitch this kind has (free kick / corner / none), and the layouts it offers — before the early return
  // below, because hooks must run on every render.
  const zoneKind = KIND_CAPABILITIES[activeKind].zoneSelector;
  const layoutOptions = useMemo<Array<[string, string]>>(() => {
    if (zoneKind === 'freekick') return Array.from(new Map(freeKickZones.map((z) => [z.layoutId, z.layoutName])).entries());
    if (zoneKind === 'corner') return Array.from(new Map(cornerZoneLayouts.map((l) => [l.layoutId, l.layoutName])).entries());
    return [];
  }, [zoneKind, freeKickZones, cornerZoneLayouts]);
  // Corners: which side's corners to show, as two pills with their kick counts (the filtered kicks, like the pitch).
  const cornerSideCounts = useMemo(() => ({
    Left: zonePitchMeasurements.filter((m) => cornerSideOf(m) === 'Left').length,
    Right: zonePitchMeasurements.filter((m) => cornerSideOf(m) === 'Right').length,
  }), [zonePitchMeasurements]);

  if (!focusedPlayer) return null;

  const cornerSidePills = activeKind === 'Corner' ? (['Left', 'Right'] as const).map((side) => (
    <button
      key={side}
      type='button'
      className={`sessions-preview-smart-pill${selectedCornerSide === side ? ' sessions-preview-smart-pill--active' : ''}`}
      aria-pressed={selectedCornerSide === side}
      onClick={() => { playerPerformanceStore.setCornerSide(side); clearZones(); }}
      data-test={`pp-corner-side-${side}`}
    >
      {t(`playerPerformance.cornerSide${side}`)} ({cornerSideCounts[side]})
    </button>
  )) : null;
  const zonesChip = selectedZones.size > 0 ? (
    <button
      type='button'
      className='sessions-preview-smart-pill sessions-preview-smart-pill--active sessions-preview-smart-pill--chip'
      onClick={clearZones}
      data-test='pp-zones-chip'
    >
      <span>{t('playerPerformance.zonesSelected', { count: selectedZones.size })}</span>
      <span className='sessions-preview-smart-pill-close' aria-hidden='true'>&times;</span>
    </button>
  ) : null;

  const layoutPill = (
    <LayoutFilterPill
      layouts={layoutOptions}
      selectedId={zoneKind === 'freekick' ? selectedFreeKickLayoutId : selectedCornerLayoutId}
      onSelect={(layoutId) => {
        if (zoneKind === 'freekick') playerPerformanceStore.setFreeKickLayoutId(layoutId);
        else playerPerformanceStore.setCornerLayoutId(layoutId);
        clearZones();
      }}
    />
  );
  const zonePitch = zoneKind === 'freekick' ? (
    <FreeKickZonePitch
      zones={freeKickZones}
      measurements={zonePitchMeasurements}
      selectedLayoutId={selectedFreeKickLayoutId}
      selectedZones={selectedZones}
      onToggleZone={toggleZone}
    />
  ) : (
    <CornerZonePitch
      layouts={cornerZoneLayouts}
      measurements={zonePitchMeasurements}
      selectedLayoutId={selectedCornerLayoutId}
      selectedSide={selectedCornerSide}
      selectedZones={selectedZones}
      onSelectSide={(side) => {
        playerPerformanceStore.setCornerSide(side);
        clearZones();
      }}
      onToggleZone={toggleZone}
      onClearZones={clearZones}
    />
  );
  // The layouts only exist when the kind has zones, so "has layouts" is "has a zone pitch".
  const hasZonePitch = layoutOptions.length > 0;

  // "Show in table" (from the picture's popup or the compare chart): that kick alone in the table, open in the side panel.
  const openKickInTable = (m: MeasurementMarkerDto) => {
    setSelectedIds(new Set([m.id]));
    setPanelKickId(m.id);
    setPitchComparisonId(m.id);
    setPitchKey((k) => k + 1);
    pitchPreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderChart = (key: ChartTab): React.ReactNode => {
    switch (key) {
      case 'trend':
        return (
          <>
            <div className='pp-chart-section-header'>
              <h4>{t('playerPerformance.onTargetTrendTitle')} <ChartHint hintKey='playerPerformance.chartHint_trend' /></h4>
            </div>
            <div className='pp-trend-picker'>
              {TREND_GROUP_OPTIONS.map(({ key: groupKey, labelKey }) => (
                <button
                  key={groupKey}
                  type='button'
                  className={`pp-trend-pill${trendGroupBy === groupKey ? ' pp-trend-pill--active' : ''}`}
                  onClick={() => setTrendGroupBy(groupKey)}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
            <OnTargetTrend points={chartTrendPoints} groupBy={trendGroupBy} kind={activeKind} />
          </>
        );
      case 'kicksPerSession':
        return <KicksPerSessionGoalRateChart measurements={chartMeasurements} />;
      case 'restGap':
        return <RestGapGoalRateChart measurements={chartMeasurements} sessions={sessions} />;
      case 'results':
        return <ResultBreakdownChart measurements={chartMeasurements} kind={activeKind} />;
      case 'compare':
        return (
          <>
            <h4>{t('playerPerformance.compareChartTitle')} <ChartHint hintKey='playerPerformance.chartHint_compare' /></h4>
            <MetricsCompareChart
              measurements={chartMeasurements}
              kind={activeKind}
              onShowInTable={openKickInTable}
            />
          </>
        );
    }
  };

  // One chart card by default; "+ Add chart" adds a second row. A chart shown in another card is disabled in the switcher.
  const selectChart = (slot: number, key: ChartTab) => setChartSlots((prev) => prev.map((k, i) => (i === slot ? key : k)));
  const addChart = () => {
    setChartSlots((prev) => {
      const free = CHART_TABS.find((c) => !prev.includes(c.key));
      return free ? [...prev, free.key] : prev;
    });
    markLayoutMoving();
    setOverviewCollapsed(true);
  };
  const removeChart = (slot: number) => {
    setChartSlots((prev) => prev.filter((_, i) => i !== slot));
    markLayoutMoving();
    setOverviewCollapsed(false);
  };

  return (
    <div data-test='pp-performance-view'>
      {isLoading ? (
        <div className='pp-report-loading' data-test='pp-player-loading'>
          <LoadingOverlay isVisible contained />
          <p className='pp-report-loading-note'>{t('playerPerformance.loadingPlayerData')}</p>
        </div>
      ) : notReady ? (
        <div className='player-performance-error' data-test='pp-player-not-ready'>
          <p>{t('playerPerformance.dataNotReady')}</p>
          {onRetry && (
            <AppButton type='button' onClick={onRetry} data-testid='pp-player-retry'>
              {t('playerPerformance.retry')}
            </AppButton>
          )}
        </div>
      ) : loadError ? (
        <div className='player-performance-error' data-test='pp-player-error'>
          <p>{loadError}</p>
          {onRetry && (
            <AppButton type='button' onClick={onRetry} data-testid='pp-player-retry'>
              {t('playerPerformance.retry')}
            </AppButton>
          )}
        </div>
      ) : tabs.length === 0 ? (
        <div className='player-performance-empty' data-test='pp-player-empty'>
          <p>{t('playerPerformance.snapshotNoPlayerMeasurements')}</p>
        </div>
      ) : (
      <>
      <SessionTypeTabs tabs={tabs} activeFilterValue={activeTab?.filterValue ?? sessionTypeFilter} onSelect={onSelectSessionType} />

      {isGeneralTab ? (
        <div className='pp-section' data-test='pp-general-tab'>
          <h4>{t('playerPerformance.generalOverviewTitle', { name: focusedPlayer.name })}</h4>
          {generalSummary && (
            <p className='pp-hint'>
              {t('playerPerformance.generalOverviewSubtitle', {
                sessionCount: generalSummary.sessionCount,
                measurementCount: generalSummary.measurementCount,
                lastSessionDate: generalSummary.lastSessionDate,
                lastSessionMeasurementCount: generalSummary.lastSessionMeasurementCount,
                sessionType: t(generalSummary.lastSessionKindLabelKey),
              })}
            </p>
          )}
          {generalSummary && (
            <div className='pp-general-summary-row' data-test='pp-general-summary-row'>
              <div className='kick-stats-item kick-stats-item--no-hover pp-general-summary-metric pp-general-summary-metric--centered'>
                <div className='kick-stats-title-group'>
                  <div className='kick-stats-title'>{t('playerPerformance.snapshotSessions')}</div>
                </div>
                <div className='kick-stats-value-row'>
                  <div className='kick-stats-value'>{generalSummary.sessionCount}</div>
                </div>
              </div>

              <div className='kick-stats-item kick-stats-item--no-hover pp-general-summary-metric pp-general-summary-metric--centered'>
                <div className='kick-stats-title-group'>
                  <div className='kick-stats-title'>{t('playerPerformance.snapshotMeasurements')}</div>
                </div>
                <div className='kick-stats-value-row'>
                  <div className='kick-stats-value'>{generalSummary.measurementCount}</div>
                </div>
              </div>

              <div className='pp-general-summary-card pp-general-summary-card--activity'>
                <div className='pp-general-summary-head'>
                  <span className='pp-discipline-card-stat-label pp-general-summary-title'>{t('playerPerformance.activityLast30DaysTitle')}</span>
                </div>
                <PlayerPerformanceActivityLast30Days measurements={focusedPlayerMeasurements} />
              </div>
            </div>
          )}
          <div className='pp-general-grid'>
            {disciplineSummaries.map((summary) => (
              <PlayerPerformanceDisciplineCard
                key={summary.filterValue}
                title={t('playerPerformance.disciplineSessionsTitle', { discipline: t(summary.labelKey) })}
                kind={summary.kind}
                sessionCount={summary.sessionCount}
                kickCount={summary.kickCount}
                points={summary.trendPoints}
                onMoreDetails={() => onSelectSessionType(summary.filterValue)}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
        <div className={`pp-kind-layout${chartsFocused ? ' pp-kind-layout--charts' : ''}${chartSlots.length > 1 ? ' pp-kind-layout--compare' : ''}${overviewCollapsed ? ' pp-kind-layout--collapsed' : ''}${layoutMoving ? ' pp-kind-layout--moving' : ''}`} data-test='pp-kind-layout'>
          <div className='pp-kind-col' data-test='pp-kind-center'>
          <PitchPreviewCard
            key={`${focusedPlayer.id}-${activeKey}`}
            title={t('playerPerformance.overviewTitle')}
            subtitle={t('playerPerformance.overviewHint', { noun: kickNoun(activeKind) })}
            selectedSessionLabel={sessionFilter?.label ?? null}
            onClearSelectedSession={() => setSessionFilter(null)}
            playerId={focusedPlayer.id}
            sessionType={activeKey}
            result={result}
            favorite={favorite}
            initialView={prefersTopView(activeKind) ? 'top' : 'goal'}
            initialViewKey={`${focusedPlayer.id}-${activeKey}`}
            initialComparisonId={pitchComparisonId}
            players={[{ id: focusedPlayer.id, name: focusedPlayer.name }]}
            sessionTypeOptions={[{ value: activeKey, label: activeTabLabel }]}
            resultOptions={resultOptions}
            onPlayerChange={() => undefined}
            onSessionTypeChange={() => undefined}
            onResultChange={setResult}
            onFavoriteChange={setFavorite}
            sideFilter={sideFilter}
            onSideFilterChange={setSideFilter}
            onReset={() => { setResult([]); setFavorite('all'); setSideFilter(new Set()); clearSelection(); clearZones(); }}
            onOpenAnalysis={() => undefined}
            changedField={null}
            flashCard={false}
            previewMeasurements={topViewMeasurements}
            sessions={sessions}
            selectedIds={selectedIds}
            onDragSelected={addSelected}
            onToggleKick={toggleKick}
            onClearSelection={clearSelection}
            hidePlayerControl={true}
            hideSessionTypeControl={true}
            hideAnalysisLink={true}
            hideSideFilter={activeKind === 'Corner'}
            hideTableView
            hideSummary
            landscapeTop
            hide3DView
            favoriteFilterAvailable={hasFavorites}
            showSelectionChip
            resultFilterAsModal
            extraFilters={hasZonePitch ? <>{layoutPill}{cornerSidePills}{zonesChip}</> : undefined}
            extraFiltersActive={selectedZones.size > 0}
            collapsed={overviewCollapsed}
            onToggleCollapse={() => { markLayoutMoving(); setOverviewCollapsed((v) => !v); }}
            topViewContent={hasZonePitch ? zonePitch : undefined}
            simplifiedKickModal={true}
            getSessionKickCount={(m) => sessionKickCount(measurements, m)}
            onShowInTable={openKickInTable}
            onFilterSession={filterToSession}
            kind={activeKind}
          />
          </div>
          <div data-test='pp-scoped-count' hidden>{scopedMeasurements.length}</div>

          <button
            type='button'
            className='pp-kind-swap'
            onClick={() => { markLayoutMoving(); setChartsFocused((v) => !v); }}
            aria-pressed={chartsFocused}
            aria-label={t('playerPerformance.layoutChangeWidth')}
            title={t('playerPerformance.layoutChangeWidth')}
            data-test='pp-kind-swap'
          >
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
              <path d='M2 5.5h11M10.5 3l2.5 2.5-2.5 2.5M14 10.5H3M5.5 8L3 10.5 5.5 13' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>

          <div className='pp-kind-col pp-chart-slots' data-test='pp-chart-slots'>
            <div className='pp-chart-slots__inner'>
            {chartSlots.map((chart, slot) => (
              <ChartSwitcherCard
                key={slot}
                slot={slot}
                options={CHART_TABS}
                active={chart}
                disabledKeys={chartSlots.filter((_, i) => i !== slot)}
                onSelect={(key) => selectChart(slot, key)}
                action={chartSlots.length > 1 ? (
                  <button type='button' className='pp-chart-slot__add' onClick={() => removeChart(slot)} data-test={`pp-chart-remove-${slot}`}>
                    {t('playerPerformance.removeChart')}
                  </button>
                ) : (
                  <button type='button' className='pp-chart-slot__add' onClick={addChart} data-test='pp-chart-add'>
                    {t('playerPerformance.addChart')}
                  </button>
                )}
              >
                {renderChart(chart)}
              </ChartSwitcherCard>
            ))}
            </div>
          </div>
        </div>

        <div className={`pp-kind-bottom${panelKick ? ' pp-kind-bottom--detail' : ''}`} data-test='pp-kind-bottom'>
        {panelKick ? (
          <div className='pp-kind-tiles' data-test='pp-kind-panel-host'>
            <KickDetailPanel measurement={panelKick} sessionKickCount={panelSessionKickCount} onClose={() => setPanelKickId(null)} />
          </div>
        ) : (
          <div className='pp-kind-tiles' data-test='pp-kind-tiles'>
            <KickStatsSummaryRow
              totalSessions={playerKindSessionCount}
              totalKicks={rowStats.totalKicks}
              onTargetPct={rowStats.onTargetPct}
              avgBallSpeed={rowStats.avgBallSpeed}
              avgSpinRate={rowStats.avgSpinRate}
              avgPullDirection={rowStats.avgPullDirection}
              avgSpinAxis={rowStats.avgSpinAxis}
              avgWallCrossingHeight={rowStats.avgWallCrossingHeight}
              kind={activeKind}
            />
          </div>
        )}
        <div ref={pitchPreviewRef} className='sessions-vis-card pp-kind-table' data-test='pp-kind-table'>
          <div className='kick-locations-header'>
            <div className='kick-locations-title'><h3>{t('sessions.viewTable')}</h3></div>
          </div>
          <div className='sessions-kick-locations-view sessions-kick-locations-view--table'>
            <MeasurementsTable
              key={pitchKey}
              data={tableMeasurements}
              sessions={sessions}
              selectedIds={selectedIds}
              onToggleKick={toggleKick}
              showSelectionControls
              onMoreInfo={(m) => setPanelKickId(m.id)}
              kind={activeKind}
              initialComparisonId={pitchComparisonId}
              activeMeasurementId={panelKick?.id ?? null}
            />
          </div>
        </div>
        </div>
        </>
      )}
      </>
      )}
    </div>
  );
});

export default PlayerPerformanceView;
